/**
 * Countries that traffic from a restricted jurisdiction commonly transits through.
 *
 * People in these regions typically reach the internet via a neighbouring
 * country's network — cellular roaming across a land border, a leased line into an
 * adjacent hub, or a VPN endpoint in the nearest large market. When a visitor's
 * browser implies a restricted jurisdiction and their IP exits in one of these
 * countries, the pairing is consistent with that pattern rather than coincidental.
 *
 * These are observed routing patterns, NOT accusations and NOT a screening rule.
 * A visitor exiting in a corridor country may simply live there.
 */
export interface TransitCorridor {
  /** Restricted jurisdiction the browser region implies. */
  origin: string
  /** ISO codes commonly seen as the exit point. */
  via: string[]
  note: string
}

export const transitCorridors: TransitCorridor[] = [
  {
    origin: 'IR',
    via: ['TR', 'AE', 'AM', 'IQ', 'DE', 'NL'],
    note: 'Turkey and the UAE are the dominant hubs; Armenia and Iraq via land routes'
  },
  {
    origin: 'SY',
    via: ['LB', 'TR', 'JO', 'AE'],
    note: 'Lebanon and Turkey carry most cross-border capacity'
  },
  {
    origin: 'AF',
    via: ['PK', 'IR', 'AE', 'TR'],
    note: 'Pakistan and Iran border routes, UAE for satellite and leased lines'
  },
  {
    origin: 'CU',
    via: ['PA', 'MX', 'ES', 'US'],
    note: 'Panama and Mexico as regional hubs; Spain via historic links'
  },
  {
    origin: 'KP',
    via: ['CN', 'RU'],
    note: 'Effectively all external routing transits China or Russia'
  },
  {
    origin: 'BY',
    via: ['RU', 'PL', 'LT', 'UA'],
    note: 'Russia for upstream transit, EU neighbours for circumvention'
  },
  {
    origin: 'RU',
    via: ['KZ', 'AM', 'GE', 'TR', 'RS', 'AE'],
    note: 'Common relocation and VPN endpoints since 2022'
  },
  {
    origin: 'VE',
    via: ['CO', 'PA', 'US', 'ES'],
    note: 'Colombia and Panama as regional hubs'
  }
]

const BY_ORIGIN = new Map(transitCorridors.map(entry => [entry.origin, entry]))

/** Whether an exit country is a known transit route for that origin. */
export function isKnownCorridor(origin: string, exitCountry: string): boolean {
  const corridor = BY_ORIGIN.get(origin.trim().toUpperCase())
  return corridor ? corridor.via.includes(exitCountry.trim().toUpperCase()) : false
}

export function corridorFor(origin: string): TransitCorridor | null {
  return BY_ORIGIN.get(origin.trim().toUpperCase()) ?? null
}
